/* eslint-disable camelcase */
import { getCompanyFirebaseToken } from "@api/auth";
import {
    getBureauCompanies,
    getCompanyCostCenters,
    getCompanyDetails,
    getInvoice,
    patchInvoice,
    patchPatent,
    patchRenewal,
} from "@api/company";
import { saveState } from "@store/local-storage";
import { mapDefaultStateFromSchema } from "@store/schema";
import { requiredParam } from "@utils/func";
import { format, startOfYear } from "date-fns";
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import Vue from "vue";

const getStartDate = () => {
  const now = new Date();
  now.setFullYear(now.getFullYear() - 1);
  const startDate = startOfYear(now);
  const formattedDate = format(startDate, "yyyy-MM-dd");
  return formattedDate;
};

// ---------------------
// STATE SCHEMA
// ---------------------

const stateSchema = {
  // -----------
  // AUTH
  // -----------
  firebaseToken: {
    default: {
      token: undefined,
      timestamp: 0,
      companyId: "",
    },
    saveToLocalStorage: true,
  },

  // -----------
  // COMPANY DETAILS
  // -----------
  id: {
    default: "",
    dbKey: "company_id",
    saveToLocalStorage: true,
  },
  isBureauCompany: {
    default: false,
    dbKey: "is_bureau_company",
    saveToLocalStorage: true,
  },
  logoUrl: {
    default: "",
    dbKey: "logo_url",
    saveToLocalStorage: true,
  },
  name: {
    default: "",
    dbKey: "name",
    saveToLocalStorage: true,
  },

  // -----------
  // PATENTS
  // -----------
  patents: {
    default: {},
    saveToLocalStorage: false,
  },
  patentsHasLoaded: {
    default: false,
    saveToLocalStorage: false,
  },

  years: {
    default: [],
    saveToLocalStorage: false,
  },

  months: {
    default: [],
    saveToLocalStorage: false,
  },

  monthsInYears: {
    default: [],
    saveToLocalStorage: false,
  },

  // -----------
  // RENEWALS
  // -----------
  renewals: {
    default: {},
    saveToLocalStorage: false,
  },
  renewalsHasLoaded: {
    default: false,
    saveToLocalStorage: false,
  },

  // -----------
  // RENEWAL NOTIFICATIONS
  // -----------
  renewalNotifications: {
    default: {},
    saveToLocalStorage: false,
  },
  renewalNotificationsHasLoaded: {
    default: false,
    saveToLocalStorage: false,
  },

  // -----------
  // Invoices
  // -----------
  invoices: {
    default: {},
    saveToLocalStorage: false,
  },
  invoicesHasLoaded: {
    default: false,
    saveToLocalStorage: false,
  },
  invoiceTypes: {
    default: [],
    dbKey: "invoice_types",
    saveToLocalStorage: false,
  },

  // -----------
  // CLIENTS
  // -----------
  clients: {
    default: [],
    saveToLocalStorage: false,
  },
  clientsHasLoaded: {
    default: false,
    saveToLocalStorage: true,
  },

  // -----------
  // Sort
  // -----------
  sort: {
    default: [],
    saveToLocalStorage: true,
  },

  // -----------
  // COST CENTERS
  // -----------
  costCenters: {
    default: [],
    saveToLocalStorage: false,
  },
  costCentersHasLoaded: {
    default: false,
    saveToLocalStorage: false,
  },
};

// ---------------------
// STATE
// ---------------------

export const state = {
  ...mapDefaultStateFromSchema({
    moduleName: "company",
    stateSchema,
  }),
};

// ---------------------
// GETTERS
// ---------------------

export const getters = {
  // ----------
  // COMPANY IS LOGGED IN
  // ----------
  companyIsLoggedIn(state) {
    return !!state.id;
  },

  // ----------
  // MAPPED PATENTS DATA HAS LOADED
  // ----------
  mappedPatentsDataHasLoaded(state) {
    return state.patentsHasLoaded;
  },

  // ----------
  // MAPPED PATENTS
  // ----------
  mappedPatents(state, getters) {
    const patents = JSON.parse(JSON.stringify(state.patents));
    const renewals = JSON.parse(JSON.stringify(state.renewals));
    const renewalNotifications = JSON.parse(
      JSON.stringify(state.renewalNotifications)
    );
    const invoices = JSON.parse(JSON.stringify(state.invoices));

    if (state.patentsHasLoaded) {
      const data = Object.keys(patents).reduce(
        (result, groupKey) => {
          patents[groupKey].forEach((patent) => {
            const renewal = renewals[patent.renewal_id] || {};
            const invoice = invoices[renewal.invoice_id] || {};
            const renewalNotification =
              renewalNotifications[renewal.invoice_id] || {};

            result[groupKey].push({
              ...patent,
              renewal,
              invoice,
              renewalNotification,
            });
          });

          return result;
        },
        {
          active_patents: [],
          verifying_patents: [],
          archived_patents: [],
          all_patents: [],
          to_be_renewed_patents: [],
        }
      );

      return data;
    }
  },
};

// ---------------------
// MUTATIONS
// ---------------------

export const mutations = {
  // ----------
  // COMPANY MUTATIONS
  // ----------
  SET_COMPANY_STATE(
    state,
    { key = requiredParam("key"), newValue = requiredParam("newValue") } = {}
  ) {
    state[key] = newValue;

    if (stateSchema[key].saveToLocalStorage) {
      saveState({
        key: `company.${key}`,
        state: newValue,
      });
    }
  },

  // ----------
  // PATENT MUTATIONS
  // ----------
  SET_COMPANY_PATENT(
    state,
    {
      patentId = requiredParam("patentId"),
      patentType = "active_patents",
      newValue = requiredParam("newValue"),
    } = {}
  ) {
    const patentIndex = state.patents[patentType].findIndex(
      (patent) => patent.ft_patent_id === patentId
    );

    if (patentIndex !== -1) {
      const patent = {
        ...state.patents[patentType][patentIndex],
        ...newValue,
      };

      Vue.set(state.patents[patentType], patentIndex, patent);
    }
  },

  DELETE_COMPANY_PATENT(
    state,
    { patentId = requiredParam("patentId"), patentType = "active_patents" } = {}
  ) {
    const patentIndex = state.patents[patentType].findIndex(
      (patent) => patent.ft_patent_id === patentId
    );

    if (patentIndex !== -1) {
      state.patents[patentType].splice(patentIndex, 1);
    }
  },

  // ----------
  // RENEWAL MUTATIONS
  // ----------
  SET_COMPANY_RENEWAL(
    state,
    {
      renewalId = requiredParam("renewalId"),
      newValue = requiredParam("newValue"),
    } = {}
  ) {
    Vue.set(state.renewals, renewalId, newValue);
  },

  DELETE_COMPANY_RENEWAL(
    state,
    { renewalId = requiredParam("renewalId") } = {}
  ) {
    Vue.delete(state.renewals, renewalId);
  },

  // ----------
  // INVOICE MUTATIONS
  // ----------
  SET_COMPANY_INVOICE(
    state,
    {
      invoiceId = requiredParam("invoiceId"),
      newValue = requiredParam("newValue"),
    } = {}
  ) {
    Vue.set(state.invoices, invoiceId, newValue);
  },

  DELETE_COMPANY_INVOICE(
    state,
    { invoiceId = requiredParam("invoiceId") } = {}
  ) {
    Vue.delete(state.invoices, invoiceId);
  },
};

// ---------------------
// ACTIONS
// ---------------------

export const actions = {
  // ----------
  // AUTHENTICATE FIREBASE
  // ----------
  async authenticateCompanyFirebase(
    { state, commit },
    { forceTokenRefresh = false, companyId = state.id } = {}
  ) {
    // Initialize firebase if there are no firebase apps
    if (!firebase.apps.length) {
      const firebaseConfig = JSON.parse(
        process.env.NODE_ENV === "development"
          ? process.env.VUE_APP_FIREBASE_CONFIG_DEV
          : process.env.VUE_APP_FIREBASE_CONFIG
      );

      await firebase.initializeApp(firebaseConfig);
    }

    // Check if selected companyId matches the currently signed in companyId
    const companyIdMatchesFirebaseTokenCompanyId =
      state.firebaseToken.companyId === companyId;

    // Check if the firebase token has expired
    const firebaseTokenHasExpired =
      Date.now() - state.firebaseToken.timestamp >= 24 * 55 * 1000;

    if (
      !companyIdMatchesFirebaseTokenCompanyId ||
      firebaseTokenHasExpired ||
      forceTokenRefresh
    ) {
      const token = await getCompanyFirebaseToken({ companyId });

      await firebase.auth().signInWithCustomToken(token);
      commit("SET_COMPANY_STATE", {
        key: "firebaseToken",
        newValue: {
          token,
          timestamp: Date.now(),
          companyId,
        },
      });
    }
  },
  // ----------
  // FETCH COMPANY
  // ----------
  async fetchCompany({ state, commit }, { companyId = state.id } = {}) {
    const companyDetails = await getCompanyDetails({ companyId });
    const companyClients = await getBureauCompanies({ companyId });
    const details = companyDetails.data || {};
    const clients = companyClients.data || {};

    // Set company details
    Object.keys(details).forEach((dbKey) => {
      const schemaMatch = Object.entries(stateSchema).find(
        (schemaEntry) => schemaEntry[1].dbKey === dbKey
      );

      if (schemaMatch) {
        const key = schemaMatch[0];
        const dbValue = details[dbKey];
        const defaultValue = stateSchema[key].default;
        const newValue = dbValue || defaultValue;

        commit("SET_COMPANY_STATE", {
          key,
          newValue,
        });
      }
    });

    // Set clients
    commit("SET_COMPANY_STATE", {
      key: "clients",
      newValue: clients,
    });

    commit("SET_COMPANY_STATE", {
      key: "clientsHasLoaded",
      newValue: true,
    });

    return companyDetails;
  },

  // ----------
  // FETCH COST CENTERS
  // ----------
  async fetchCompanyCostCenters(
    { state, commit },
    { companyId = state.id } = {}
  ) {
    commit("SET_COMPANY_STATE", {
      key: "costCentersHasLoaded",
      newValue: false,
    });

    const response = await getCompanyCostCenters({ companyId });
    const costCenters = response.data.reduce((result, costCenter) => {
      result.push(costCenter);
      return result;
    }, []);

    commit("SET_COMPANY_STATE", {
      key: "costCenters",
      newValue: costCenters,
    });

    commit("SET_COMPANY_STATE", {
      key: "costCentersHasLoaded",
      newValue: true,
    });
  },

  // ----------
  // FETCH PATENTS
  // ----------
  async fetchCompanyPatents(
    { state, commit, dispatch },
    { companyId = state.id } = {}
  ) {
    const companyIds = [
      state.id,
      ...state.clients.map((client) => client.customer.company_id),
    ];

    const months = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12,
    };

    const monthsInYears = [];

    const years = [];
    const activeMonths = [];
    const activeDays = [];

    const startDate = getStartDate();

    const patentPromises = companyIds.map((id) => {
      const patentsRef = firebase
        .firestore()
        .collection("middletier")
        .doc(id)
        .collection("patents")
        .where("renewal_date", ">=", startDate);

      return patentsRef.get();
    });

    Promise.all(patentPromises).then((response) => {
      const patents = response.reduce(
        (result, snapshot) => {
          const activeCompanyPatents = [];
          const toBeRenewedCompanyPatents = [];
          const verifyingCompanyPatents = [];
          const archivedCompanyPatents = [];
          const allCompanyPatents = [];

          snapshot.forEach((doc) => {
            const patent = doc.data();

            if (patent.type === "trademark") {
              return;
            }
            
            allCompanyPatents.push({
              ...patent,
              renewal: {},
              invoice: {},
            });

            const months = [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ];

            const year = patent.is_force_surcharge
              ? new Date(patent.surcharge_date).getYear() + 1900
              : new Date(patent.renewal_date).getYear() + 1900;
            const monthIndex = patent.is_force_surcharge
              ? new Date(patent.surcharge_date).getMonth()
              : new Date(patent.renewal_date).getMonth();
            const dayIndex = patent.is_force_surcharge
              ? new Date(patent.surcharge_date).getDate()
              : new Date(patent.renewal_date).getDate();
            const days = [...Array(32).keys()];

            // patent.formatted_renewal_date === patent.renewal_date ? patent.renewal.renewal_date : undefined;

            if (patent.patent_cost_center_name !== patent.patent_company_name) {
              patent.client = patent.patent_cost_center_name;
            } else {
              patent.client = patent.patent_company_name;
            }

            if (patent.invoice && patent.invoice.sent_date) {
              patent.formatted_sent_date = patent.invoice.sent_date;
            } else if (
              patent.renewal &&
              state.invoices &&
              state.invoices[patent.renewal.invoice_id] &&
              state.invoices[patent.renewal.invoice_id]["sent_date"]
            ) {
              patent.formatted_sent_date =
                state.invoices[patent.renewal.invoice_id]["sent_date"];
            } else {
              patent.formatted_sent_date = "";
            }

            if (patent.invoice && patent.invoice.paid_date) {
              patent.formatted_paid_date = patent.invoice.paid_date;
            } else if (
              patent.renewal &&
              state.invoices &&
              state.invoices[patent.renewal.invoice_id] &&
              state.invoices[patent.renewal.invoice_id]["paid_date"]
            ) {
              patent.formatted_paid_date =
                state.invoices[patent.renewal.invoice_id]["paid_date"];
            } else {
              patent.formatted_paid_date = "";
            }

            if (patent.renewal && patent.renewal.invoice_id) {
              patent.invoice_id = patent.renewal.invoice_id;
            } else if (state.renewals[patent.renewal_id]) {
              let renewal = state.renewals[patent.renewal_id];

              patent.invoice_id = renewal.invoice_id;
            } else {
              patent.invoice_id = null;
            }

            patent.formatted_year = year === 1970 ? undefined : String(year);
            patent.formatted_month =
              year === 1970 ? undefined : months[monthIndex];
            patent.formatted_day = year === 1970 ? undefined : days[dayIndex];

            if (
              Boolean(patent.formatted_year) &&
              !years.includes(String(patent.formatted_year))
            ) {
              patent.formatted_year = String(patent.formatted_year);

              years.push(patent.formatted_year);
            }

            if (
              Boolean(patent.formatted_month) &&
              !activeMonths.includes(patent.formatted_month)
            ) {
              activeMonths.push(patent.formatted_month);
            }

            if (
              patent.is_finished ||
              typeof patent.formatted_year == "undefined"
            ) {
              return;
            } else if (!patent.formatted_year) {
              archivedCompanyPatents.push({
                ...patent,
                renewal: {},
                invoice: {},
              });
            } else if (
              ["now", "client_responsible", "on_hold", "never"].includes(
                patent.status
              ) ||
              patent.is_finished ||
              patent.is_dead
            ) {
              monthsInYears.push({
                year: patent.formatted_year,
                month: patent.formatted_month,
                status: patent.status,
              });

              activeCompanyPatents.push({
                ...patent,
                renewal: {},
                invoice: {},
              });
            } else if (
              ["manual"].includes(patent.status) &&
              !patent.is_finished &&
              !patent.is_dead
            ) {
              verifyingCompanyPatents.push({
                ...patent,
                renewal: {},
                invoice: {},
              });
            } else {
              archivedCompanyPatents.push({
                ...patent,
                renewal: {},
                invoice: {},
              });
            }

            if (
              ["now", "on_hold"].includes(patent.status) &&
              !patent.is_finished &&
              !patent.is_dead &&
              !patent.is_later
            ) {
              toBeRenewedCompanyPatents.push({
                ...patent,
                renewal: {},
                invoice: {},
              });
            }
          });

          result = {
            active_patents: [...result.active_patents, ...activeCompanyPatents],
            verifying_patents: [
              ...result.verifying_patents,
              ...verifyingCompanyPatents,
            ],
            archived_patents: [
              ...result.archived_patents,
              ...archivedCompanyPatents,
            ],
            all_patents: [...result.all_patents, ...allCompanyPatents],
            to_be_renewed_patents: [
              ...result.to_be_renewed_patents,
              ...toBeRenewedCompanyPatents,
            ],
          };

          return result;
        },
        {
          active_patents: [],
          verifying_patents: [],
          archived_patents: [],
          all_patents: [],
          to_be_renewed_patents: [],
        }
      );

      commit("SET_COMPANY_STATE", {
        key: "years",
        newValue: years.sort(),
      });

      commit("SET_COMPANY_STATE", {
        key: "months",
        newValue: activeMonths,
      });

      commit("SET_COMPANY_STATE", {
        key: "monthsInYears",
        newValue: monthsInYears,
      });

      commit("SET_COMPANY_STATE", {
        key: "patents",
        newValue: patents,
      });

      commit("SET_COMPANY_STATE", {
        key: "patentsHasLoaded",
        newValue: true,
      });

      return patents;
    });
  },

  fetchPatent(
    { state, commit },
    { patentId = requiredParam("patentId") } = {}
  ) {
    const patentRef = firebase
      .firestore()
      .collection("middletier")
      .doc(state.id)
      .collection("patents")
      .doc(patentId);

    return patentRef
      .get()
      .then((patentDoc) => {
        if (patentDoc.exists) {
          const patentData = patentDoc.data();

          commit("SET_COMPANY_PATENT", {
            patentId,
            newValue: patentData,
          });

          const renewalId = patentData.renewal_id;

          if (renewalId) {
            const renewalRef = firebase
              .firestore()
              .collection("middletier")
              .doc(state.id)
              .collection("renewals")
              .doc(renewalId);

            renewalRef.get().then((renewalDoc) => {
              const renewalData = renewalDoc.data();

              commit("SET_COMPANY_RENEWAL", {
                renewalId,
                newValue: renewalData,
              });
            });
          }
        }
      })
      .catch((error) => {
        console.error("Error getting document:", error);
      });
  },

  // ----------
  // UPDATE PATENT
  // ----------
  updatePatent(
    { commit, dispatch },
    { patentId = requiredParam("patentId"), data = requiredParam("data") } = {}
  ) {
    return patchPatent({
      patentId,
      data,
    }).then((response) => {
      return response;
    });
  },

  async fetchCompanyRenewals(
    { state, commit, dispatch },
    { companyId = state.id } = {}
  ) {
    const companyIds = [
      state.id,
      ...state.clients.map((client) => client.customer.company_id),
    ];

    const startDate = getStartDate();

    const renewalPromises = companyIds.map((id) => {
      const renewalsRef = firebase
        .firestore()
        .collection("middletier")
        .doc(id)
        .collection("renewals")
        .where("renewal_date", ">=", startDate);

      return renewalsRef.get();
    });

    let stored = [];

    Promise.all(renewalPromises).then((response) => {
      const renewals = response.reduce((result, snapshot) => {
        snapshot.forEach((doc) => {
          const renewal = doc.data();
          const renewalId = doc.id;

          if (!stored.includes(renewalId)) {
            stored.push(renewalId);
            result[renewalId] = renewal;
          }
        });

        return result;
      }, {});

      commit("SET_COMPANY_STATE", {
        key: "renewals",
        newValue: renewals,
      });

      commit("SET_COMPANY_STATE", {
        key: "renewalsHasLoaded",
        newValue: true,
      });

      return renewals;
    });
  },

  async fetchCompanyRenewalNotifications(
    { state, commit, dispatch },
    { companyId = state.id } = {}
  ) {
    let renewals;

    const companyIds = [
      state.id,
      ...state.clients.map((client) => client.customer.company_id),
    ];

    const renewalPromises = companyIds.map((id) => {
      const renewalsRef = firebase
        .firestore()
        .collection("middletier")
        .doc(id)
        .collection("renewal-notifications");

      return renewalsRef.get();
    });

    Promise.all(renewalPromises).then((response) => {
      renewals = response.reduce((result, snapshot) => {
        const companyRenewals = {};

        snapshot.forEach((doc) => (companyRenewals[doc.id] = doc.data()));
        result = {
          ...result,
          ...companyRenewals,
        };

        return result;
      }, []);

      commit("SET_COMPANY_STATE", {
        key: "renewalNotifications",
        newValue: renewals,
      });

      commit("SET_COMPANY_STATE", {
        key: "renewalNotificationsHasLoaded",
        newValue: true,
      });

      return renewals;
    });
  },
  // ----------
  // UPDATE RENEWAL NOTIFICATION
  // ----------

  // ----------
  // UPDATE RENEWAL
  // ----------
  updateRenewal(
    { dispatch },
    {
      patentId = "",
      renewalId = requiredParam("renewalId"),
      data = requiredParam("data"),
    } = {}
  ) {
    return patchRenewal({
      renewalId,
      data,
    }).then((response) => {
      if (patentId) {
        dispatch("fetchPatent", { patentId });
      }
      return response;
    });
  },

  // ----------
  // FETCH INVOICES
  // ----------
  async fetchCompanyInvoices(
    { state, commit, dispatch },
    { companyId = state.id } = {}
  ) {
    const companyIds = [
      state.id,
      ...state.clients.map((client) => client.customer.company_id),
    ];

    const invoicePromises = companyIds.map((id) => {
      const invoicesRef = firebase
        .firestore()
        .collection("middletier")
        .doc(id)
        .collection("invoices");

      return invoicesRef.get();
    });

    Promise.all(invoicePromises).then((response) => {
      const invoices = response.reduce((result, snapshot) => {
        const companyInvoices = {};

        snapshot.forEach((doc) => {
          companyInvoices[doc.id] = doc.data();
        });
        result = {
          ...result,
          ...companyInvoices,
        };

        return result;
      }, []);

      commit("SET_COMPANY_STATE", {
        key: "invoices",
        newValue: invoices,
      });

      commit("SET_COMPANY_STATE", {
        key: "invoicesHasLoaded",
        newValue: true,
      });

      return invoices;
    });
  },
  // ----------
  // SET COL SORT
  // ----------
  setColSort({ commit }, data) {
    commit("SET_COMPANY_STATE", {
      key: "sort",
      newValue: data,
    });
  },

  // ----------
  // UPDATE INVOICE
  // ----------
  updateInvoice(
    { commit },
    {
      invoiceId = requiredParam("invoiceId"),
      data = requiredParam("data"),
    } = {}
  ) {
    return patchInvoice({
      invoiceId,
      data,
    })
      .then((response) => {
        getInvoice({ invoiceId }).then((res) => {
          const invoiceData = res.data;

          commit("SET_COMPANY_INVOICE", {
            invoiceId,
            newValue: invoiceData,
          });
        });

        return response;
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  },

  // ----------
  // RESET COMPANY
  // ----------
  resetCompany({ state, commit }) {
    Object.keys(state).forEach((key) => {
      commit("SET_COMPANY_STATE", {
        key,
        newValue: stateSchema[key].default,
      });
    });
  },
};
